// Small pure formatters shared across pages.

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const val = bytes / Math.pow(1024, i);
  return `${val >= 10 || i === 0 ? Math.round(val) : val.toFixed(1)} ${units[i]}`;
}

export function formatMb(mb: number | null | undefined): string {
  if (mb == null) return '—';
  return formatBytes(mb * 1024 * 1024);
}

/** ms → "0:30", "1:05", "1:02:03". Used for playlist item + loop durations. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** ms → "30s", "2m 30s", "1h 5m" — friendlier for at-a-glance loop totals. */
export function formatDurationWords(ms: number | null | undefined): string {
  if (ms == null) return '—';
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  return `${s}s`;
}

const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

/** Epoch ms → "just now", "5 min ago", "2 days ago", "—" for null. */
export function formatRelative(ts: number | null | undefined, now: number = Date.now()): string {
  if (ts == null) return 'never';
  const diff = ts - now;
  const abs = Math.abs(diff);
  const min = 60_000;
  const hr = 60 * min;
  const day = 24 * hr;
  if (abs < 45_000) return 'just now';
  if (abs < hr) return rtf.format(Math.round(diff / min), 'minute');
  if (abs < day) return rtf.format(Math.round(diff / hr), 'hour');
  if (abs < 30 * day) return rtf.format(Math.round(diff / day), 'day');
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** ms → "3d 4h", "5h", "12m" for uptime / offline duration. */
export function formatElapsed(ms: number): string {
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Naive English pluralizer: `plural(1,'item')`→'item', `plural(2,'item')`→'items'. */
export function plural(n: number, word: string, suffix = 's'): string {
  return n === 1 ? word : `${word}${suffix}`;
}
